/* eslint-disable prefer-destructuring */
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getCardApprovalsList } from 'src/redux/cardApprovalSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { cardApprovalTableHeaders, getAlignByColumnId } from 'src/utils/tableHeaders';
import { USER_ROLES } from 'src/utils/constants';
import {
  camelCaseString,
  getCardLimitRequestQueryParams, getFormattedDate, getFuelQuantity, isAdminUser
} from 'src/utils/getInitials';
import useAuth from 'src/context/AuthContext';
import { getCardDetails } from 'src/redux/cardSlice';
import CustomPagination from 'src/common/CustomPagination';
import ApprovalActionForm from './ApprovalActionForm';
import CancelApprovalForm from './CancelApprovalForm';
import CardSyncStatusModal from '../CardSyncStatusModal';

const CardApprovalsTable = ({
  refreshData, searchText, dateFilter, siteFilter, ...rest
}) => {
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = isAdminUser(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const isRequestor = user?.role === USER_ROLES.requestor;
  const { cardApprovalsList, totalCount, loading } = useSelector((state) => state.cardApprovals);
  const cardDetailsSelector = useSelector((state) => state.cards);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [showCardSyncModal, setShowCardSyncModal] = useState(false);
  const approvalAlignById = useMemo(() => getAlignByColumnId(cardApprovalTableHeaders), []);

  useEffect(() => {
    setPage(0);
    dispatch(getCardApprovalsList({
      limit,
      pageNo: 1,
      params: getCardLimitRequestQueryParams(searchText, dateFilter, siteFilter)
    }));
  }, [dispatch, limit, refreshData, searchText, dateFilter, siteFilter]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getCardApprovalsList({
      limit,
      pageNo: newPage + 1,
      params: getCardLimitRequestQueryParams(searchText, dateFilter, siteFilter)
    }));
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const showSyncStatus = (cardId) => {
    setShowCardSyncModal(true);
    dispatch(getCardDetails(cardId)).unwrap();
  };

  const onCardSyncModalClose = () => setShowCardSyncModal(false);

  const getStatusColor = (card) => {
    let statusColor = 'info';
    if (card.status === 'approved' || card.status === 'completed') {
      statusColor = 'success';
    } else if (card.status === 'rejected') {
      statusColor = 'error';
    } else if (card.status === 'cancelled') {
      statusColor = 'secondary';
    } else if (card.status === 'expired') {
      statusColor = 'warning';
    }
    return statusColor;
  };

  const handleModalShow = (card, isApproval) => {
    setCardData(card);
    if (isApproval) {
      setShowApprovalModal(true);
    } else {
      setShowCancelRequestModal(true);
    }
  };

  const handleModalClose = (isApproval = true) => {
    if (isApproval) {
      setShowApprovalModal(false);
    } else {
      setShowCancelRequestModal(false);
    }
    setCardData(null);
  };

  const displayData = cardApprovalsList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <ApprovalActionForm open={showApprovalModal} handleClose={handleModalClose} data={cardData} />
          <CancelApprovalForm open={showCancelRequestModal} handleClose={() => handleModalClose(false)} data={cardData} />
          <CardSyncStatusModal
            open={showCardSyncModal}
            onClose={onCardSyncModalClose}
            loading={cardDetailsSelector.loading}
            data={cardDetailsSelector.cardDetails}
          />
          <Card
            sx={{
              ...rest?.sx,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                minHeight: 0,
                flex: 1,
                overflow: 'auto',
                '& .MuiTableCell-stickyHeader': {
                  backgroundColor: 'background.paper',
                  zIndex: 2
                }
              }}
            >
              <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
                <Table stickyHeader>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    headCells={cardApprovalTableHeaders}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {displayData
                      .map((card) => (
                        <TableRow hover key={card.id}>
                          <TableCell align={approvalAlignById.id}>{card.id}</TableCell>
                          <TableCell align={approvalAlignById.card_uid}>
                            {isAdmin
                              ? (
                                <Link component="button" variant="body2" onClick={() => showSyncStatus(card.card)}>
                                  {card.card_uid}
                                </Link>
                              ) : card.card_uid }
                          </TableCell>
                          <TableCell align={approvalAlignById.vehicle_registration_number}>
                            {card.vehicle_registration_number}
                          </TableCell>
                          {/* <TableCell>{getFuelQuantity(card.tank_capacity)}</TableCell> */}
                          <TableCell align={approvalAlignById.requested_quantity}>{getFuelQuantity(card.requested_quantity, card.requested_quantity_gallons)}</TableCell>
                          <TableCell align={approvalAlignById.requested_on}>{getFormattedDate(card.requested_on)}</TableCell>
                          <TableCell align={approvalAlignById.approved_quantity}>{getFuelQuantity(card.approved_quantity, card.approved_quantity_gallons)}</TableCell>
                          <TableCell align={approvalAlignById.approved_or_rejected_on}>{getFormattedDate(card.approved_or_rejected_on)}</TableCell>
                          <TableCell align={approvalAlignById.filled_quantity}>{getFuelQuantity(card.filled_quantity, card.filled_quantity_gallons)}</TableCell>
                          <TableCell align={approvalAlignById.filled_on}>{getFormattedDate(card.filled_on)}</TableCell>
                          <TableCell align={approvalAlignById.remarks}>{card.remarks || 'NA'}</TableCell>
                          <TableCell align={approvalAlignById.status}>
                            <Chip
                              label={camelCaseString(card.status)}
                              color={getStatusColor(card)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align={approvalAlignById.action}>
                            {(isAdmin || isApprover) && (
                            <Button
                              variant="contained"
                              disabled={card.status !== 'pending'}
                              onClick={() => handleModalShow(card, true)}
                              size="small"
                            >
                              Review
                            </Button>
                            )}
                            {isRequestor && (
                            <Button
                              variant="contained"
                              disabled={card.status !== 'pending'}
                              onClick={() => handleModalShow(card)}
                              size="small"
                            >
                              Cancel
                            </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
              }}
            >
              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
              />
            </Box>
          </Card>
        </>
      )}
    </>
  );
};

CardApprovalsTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  dateFilter: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CardApprovalsTable;
